import { useState, useEffect, useCallback } from "react";
import {
  HiOutlineUserGroup, HiOutlinePlus, HiOutlinePencil, HiOutlineTrash,
  HiOutlineXMark, HiOutlineSparkles, HiOutlineArrowRight,
  HiOutlineExclamationTriangle, HiOutlineCheckCircle, HiOutlineUsers,
} from "react-icons/hi2";
import { retreatCohortsAPI } from "../services/api";
import { useCurrentAdmin, hasPermission } from "./permissions";
import toast from "react-hot-toast";
import "./CohortManager.css";

/* ── Create / edit cohort modal ── */
function CohortModal({ blockId, cohort, onClose, onSaved }) {
  const isEdit = !!cohort;
  const [name,     setName]     = useState(cohort?.name || "");
  const [capacity, setCapacity] = useState(cohort?.capacity ?? 12);
  const [notes,    setNotes]    = useState(cohort?.notes || "");
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState("");

  const handleSave = async () => {
    if (!name.trim()) { setError("Please give this cohort a name."); return; }
    const cap = parseInt(capacity);
    if (!cap || cap < 1) { setError("Capacity must be at least 1."); return; }

    setSaving(true); setError("");
    try {
      const payload = { name: name.trim(), capacity: cap, notes: notes.trim() || null };
      if (isEdit) await retreatCohortsAPI.update(blockId, cohort.id, payload);
      else        await retreatCohortsAPI.create(blockId, payload);
      toast.success(isEdit ? "Cohort updated." : "Cohort created.");
      onSaved();
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  const currentCount = cohort?.members?.length ?? 0;
  const shrinkingBelowCount = isEdit && parseInt(capacity) > 0 && parseInt(capacity) < currentCount;

  return (
    <div className="aap__contact-overlay" onClick={onClose}>
      <div className="aap__contact-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: "420px" }}>
        <div className="aap__contact-head">
          <h3>{isEdit ? "Edit Cohort" : "New Cohort"}</h3>
          <button type="button" onClick={onClose}><HiOutlineXMark /></button>
        </div>

        <div className="cm__field">
          <label>Name</label>
          <input className="arb__form-input" value={name} placeholder="e.g. Cohort A"
            onChange={e => { setName(e.target.value); setError(""); }} />
        </div>

        <div className="cm__field">
          <label>Ceremony Capacity</label>
          <input type="number" min="1" className="arb__form-input" value={capacity}
            onChange={e => { setCapacity(e.target.value); setError(""); }} />
          <p className="cm__field-hint">How many people can be in ceremony together at once — typically 12–15.</p>
        </div>

        {shrinkingBelowCount && (
          <div className="cm__warn">
            <HiOutlineExclamationTriangle />
            <span>
              This cohort currently has {currentCount} {currentCount === 1 ? "person" : "people"}. Setting capacity
              to {capacity} will leave it over capacity — you can still save, but you'll need to move
              {" "}{currentCount - parseInt(capacity)} out.
            </span>
          </div>
        )}

        <div className="cm__field">
          <label>Notes <span className="cm__optional">(optional)</span></label>
          <textarea className="arb__form-input" rows={2} value={notes}
            placeholder="Facilitator, room, anything worth noting…"
            onChange={e => setNotes(e.target.value)} />
        </div>

        {error && <p className="cm__error">{error}</p>}

        <div className="cm__modal-actions">
          <button type="button" className="a-btn a-btn--outline" onClick={onClose}>Cancel</button>
          <button type="button" className="a-btn a-btn--rust" disabled={saving} onClick={handleSave}>
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Cohort"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Move-to-cohort picker ── */
function MovePicker({ cohorts, currentCohortId, onPick, onClose }) {
  return (
    <div className="cm__move-menu" onClick={e => e.stopPropagation()}>
      <p className="cm__move-menu-title">Move to…</p>
      {cohorts.filter(c => c.id !== currentCohortId).map(c => (
        <button key={c.id} type="button" className="cm__move-option"
          disabled={c.isFull}
          onClick={() => { onPick(c.id); onClose(); }}>
          <span>{c.name}</span>
          <span className={`cm__move-option-count ${c.isFull ? "cm__move-option-count--full" : ""}`}>
            {c.members.length}/{c.capacity}{c.isFull ? " · Full" : ""}
          </span>
        </button>
      ))}
      {currentCohortId && (
        <button type="button" className="cm__move-option cm__move-option--unassign"
          onClick={() => { onPick(null); onClose(); }}>
          Remove from cohort
        </button>
      )}
      {cohorts.filter(c => c.id !== currentCohortId).length === 0 && !currentCohortId && (
        <p className="cm__move-empty">No other cohorts yet.</p>
      )}
    </div>
  );
}

/* ── One attendee row ── */
function AttendeeRow({ person, cohorts, currentCohortId, onMove, busy }) {
  const [showMove, setShowMove] = useState(false);

  useEffect(() => {
    if (!showMove) return;
    const close = () => setShowMove(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [showMove]);

  return (
    <div className="cm__person">
      <div className="cm__person-info">
        <p className="cm__person-name">
          {person.first_name} {person.last_name}
          {person.checked_in_at && (
            <span className="cm__checked-in" title="Checked in"><HiOutlineCheckCircle /></span>
          )}
        </p>
        <p className="cm__person-meta">
          {person.category}{person.room_type_name ? ` · ${person.room_type_name}` : ""}
        </p>
      </div>
      <div className="cm__person-action">
        <button type="button" className="a-btn a-btn--ghost cm__move-btn" disabled={busy}
          onClick={e => { e.stopPropagation(); setShowMove(v => !v); }}>
          Move <HiOutlineArrowRight />
        </button>
        {showMove && (
          <MovePicker cohorts={cohorts} currentCohortId={currentCohortId}
            onPick={cid => onMove(person.id, cid)} onClose={() => setShowMove(false)} />
        )}
      </div>
    </div>
  );
}

export default function CohortManager({ blockId }) {
  const { admin, loading: permLoading } = useCurrentAdmin();
  const canManage = permLoading ? true : hasPermission(admin, "cohorts.manage");

  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [editing,  setEditing]  = useState(null);   // null | "new" | cohort object
  const [busy,     setBusy]     = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await retreatCohortsAPI.list(blockId);
      setData(res?.data || null);
    } catch (err) {
      toast.error(err.message || "Failed to load cohorts.");
    } finally {
      setLoading(false);
    }
  }, [blockId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleMove = async (applicationId, cohortId) => {
    setBusy(true);
    try {
      const res = await retreatCohortsAPI.assign(blockId, [applicationId], cohortId);
      toast.success(res?.message || "Moved.");
      fetchData();
    } catch (err) {
      toast.error(err.message || "Couldn't move that person.");
    } finally {
      setBusy(false);
    }
  };

  const handleAutoAssign = async () => {
    setBusy(true);
    try {
      const res = await retreatCohortsAPI.autoAssign(blockId);
      toast.success(res?.message || "Assigned.");
      fetchData();
    } catch (err) {
      toast.error(err.message || "Auto-assign failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = (cohort) => {
    toast((t) => (
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <p style={{ margin: 0, fontWeight: 700 }}>Delete {cohort.name}?</p>
        <p style={{ margin: 0, fontSize: "0.78rem", color: "#6b6258", lineHeight: 1.5 }}>
          {cohort.members.length > 0
            ? `The ${cohort.members.length} ${cohort.members.length === 1 ? "person" : "people"} in it will move back to unassigned — nobody is removed from the retreat.`
            : "This cohort is empty."}
        </p>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button onClick={async () => {
            toast.dismiss(t.id); setBusy(true);
            try {
              const res = await retreatCohortsAPI.remove(blockId, cohort.id);
              toast.success(res?.message || "Cohort deleted.");
              fetchData();
            } catch (err) { toast.error(err.message || "Failed."); }
            finally { setBusy(false); }
          }} style={{ background: "#dc2626", color: "#fff", border: "none", borderRadius: "5px", padding: "0.35rem 0.75rem", cursor: "pointer", fontWeight: 700, fontSize: "0.78rem" }}>
            Delete
          </button>
          <button onClick={() => toast.dismiss(t.id)}
            style={{ background: "#f3f4f0", border: "none", borderRadius: "5px", padding: "0.35rem 0.75rem", cursor: "pointer", fontSize: "0.78rem" }}>
            Cancel
          </button>
        </div>
      </div>
    ), { duration: Infinity, style: { maxWidth: "330px" } });
  };

  if (loading) {
    return <div className="cm__loading"><div className="acr__spinner" /></div>;
  }
  if (!data) return null;

  const { cohorts, unassigned, summary } = data;

  return (
    <div className="cm">
      {/* Header + summary */}
      <div className="cm__head">
        <div className="cm__head-left">
          <p className="p-section-title" style={{ margin: 0 }}>
            <HiOutlineUserGroup className="cm__head-icon" /> Ceremony Cohorts
          </p>
          <p className="cm__head-sub">
            Ceremony runs in smaller groups than the retreat holds — split attendees into cohorts here.
          </p>
        </div>
        {canManage && (
          <div className="cm__head-actions">
            {unassigned.length > 0 && cohorts.length > 0 && (
              <button type="button" className="a-btn a-btn--outline" disabled={busy} onClick={handleAutoAssign}>
                <HiOutlineSparkles /> Auto-Assign {unassigned.length}
              </button>
            )}
            <button type="button" className="a-btn a-btn--rust" onClick={() => setEditing("new")}>
              <HiOutlinePlus /> New Cohort
            </button>
          </div>
        )}
      </div>

      {/* Summary strip */}
      {cohorts.length > 0 && (
        <div className="cm__summary">
          <div className="cm__summary-item">
            <span className="cm__summary-value">{summary.cohortCount}</span>
            <span className="cm__summary-label">Cohort{summary.cohortCount !== 1 ? "s" : ""}</span>
          </div>
          <div className="cm__summary-item">
            <span className="cm__summary-value">{summary.totalAssigned}<span className="cm__summary-of">/{summary.totalCapacity}</span></span>
            <span className="cm__summary-label">Placed</span>
          </div>
          <div className={`cm__summary-item ${summary.unassignedCount > 0 ? "cm__summary-item--alert" : ""}`}>
            <span className="cm__summary-value">{summary.unassignedCount}</span>
            <span className="cm__summary-label">Unassigned</span>
          </div>
        </div>
      )}

      {/* Empty state */}
      {cohorts.length === 0 ? (
        <div className="cm__empty">
          <HiOutlineUsers className="cm__empty-icon" />
          <p className="cm__empty-title">No cohorts yet</p>
          <p className="cm__empty-sub">
            Create your first cohort to start splitting this retreat's attendees into ceremony groups.
          </p>
          {canManage && (
            <button type="button" className="a-btn a-btn--rust" onClick={() => setEditing("new")}>
              <HiOutlinePlus /> Create First Cohort
            </button>
          )}
        </div>
      ) : (
        <div className="cm__grid">
          {cohorts.map(c => (
            <div key={c.id} className={`cm__card ${c.isOver ? "cm__card--over" : ""}`}>
              <div className="cm__card-head">
                <div>
                  <p className="cm__card-name">{c.name}</p>
                  <p className={`cm__card-count ${c.isFull ? "cm__card-count--full" : ""} ${c.isOver ? "cm__card-count--over" : ""}`}>
                    {c.members.length}/{c.capacity}
                    {c.isOver ? " · Over capacity" : c.isFull ? " · Full" : ` · ${c.remaining} open`}
                  </p>
                </div>
                {canManage && (
                  <div className="cm__card-actions">
                    <button type="button" className="a-btn a-btn--ghost" title="Edit cohort"
                      onClick={() => setEditing(c)}><HiOutlinePencil /></button>
                    <button type="button" className="a-btn a-btn--ghost" style={{ color: "var(--admin-red)" }}
                      title="Delete cohort" onClick={() => handleDelete(c)}><HiOutlineTrash /></button>
                  </div>
                )}
              </div>

              {/* Capacity bar */}
              <div className="cm__bar">
                <div className={`cm__bar-fill ${c.isOver ? "cm__bar-fill--over" : c.isFull ? "cm__bar-fill--full" : ""}`}
                  style={{ width: `${Math.min(100, (c.members.length / c.capacity) * 100)}%` }} />
              </div>

              {c.notes && <p className="cm__card-notes">{c.notes}</p>}

              <div className="cm__members">
                {c.members.length === 0 ? (
                  <p className="cm__members-empty">Nobody assigned yet.</p>
                ) : c.members.map(m => (
                  <AttendeeRow key={m.id} person={m} cohorts={cohorts} currentCohortId={c.id}
                    onMove={handleMove} busy={busy} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Unassigned pool */}
      {unassigned.length > 0 && (
        <div className="cm__unassigned">
          <div className="cm__unassigned-head">
            <p className="cm__unassigned-title">
              <HiOutlineExclamationTriangle className="cm__unassigned-icon" />
              Not in a cohort yet ({unassigned.length})
            </p>
            <p className="cm__unassigned-sub">
              These attendees have a confirmed spot on the retreat but haven't been placed into a ceremony group.
            </p>
          </div>
          <div className="cm__members">
            {unassigned.map(p => (
              <AttendeeRow key={p.id} person={p} cohorts={cohorts} currentCohortId={null}
                onMove={handleMove} busy={busy} />
            ))}
          </div>
        </div>
      )}

      {editing && (
        <CohortModal blockId={blockId}
          cohort={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchData(); }} />
      )}
    </div>
  );
}
